import { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Tabs, Table, Tag, Popconfirm, Alert, Empty, Spin, Tooltip } from 'antd';
import {
  SendOutlined,
  AppleOutlined,
  AndroidOutlined,
  DeleteOutlined,
  MobileOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { UserPushPermissionSummary, UserPushTokenDevice } from '@mygate/common';
import { notificationsService } from '@/services/notifications';
import { ApiError } from '@/services/api';
import { formatDateTime } from '@/utils/date';
import { lastReportedSuffix, noDevicesDescription } from '@/utils/push-permission';

// Status configuration for notification delivery status
const notificationStatusConfig: Record<
  string,
  { color: string; icon: React.ReactNode; label: string; description: string }
> = {
  WORKING: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    label: 'Working',
    description: 'Notifications are being delivered successfully',
  },
  FAILED: {
    color: 'error',
    icon: <CloseCircleOutlined />,
    label: 'Failed',
    description: 'Notifications failed to deliver',
  },
  PERMISSION_DENIED: {
    color: 'default',
    icon: <StopOutlined />,
    label: 'Blocked',
    description: 'User has blocked notifications on this device',
  },
  UNKNOWN: {
    color: 'warning',
    icon: <QuestionCircleOutlined />,
    label: 'Unknown',
    description: 'No notifications have been sent yet',
  },
};

const { TextArea } = Input;

interface SendNotificationModalProps {
  visible: boolean;
  userId: string | null;
  userInfo?: {
    name?: string;
    phoneNumber?: string;
  };
  onClose: () => void;
  /** Called after the backend accepted the push, before the modal closes. */
  onSent?: () => void;
  /** Prefills the form when the modal opens, e.g. from a Tradevest reminder template. */
  initialTitle?: string;
  initialBody?: string;
}

export function SendNotificationModal({
  visible,
  userId,
  userInfo,
  onClose,
  onSent,
  initialTitle,
  initialBody,
}: SendNotificationModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('send');
  const [devices, setDevices] = useState<UserPushTokenDevice[]>([]);
  const [permission, setPermission] = useState<UserPushPermissionSummary | null>(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deletingTokenId, setDeletingTokenId] = useState<string | null>(null);

  // Fetch devices when modal opens
  useEffect(() => {
    if (visible && userId) {
      fetchDevices();
      if (initialTitle || initialBody) {
        form.setFieldsValue({ title: initialTitle, body: initialBody });
      }
    }
    if (!visible) {
      // Reset state when modal closes
      setActiveTab('send');
      setDevices([]);
      setPermission(null);
      form.resetFields();
    }
  }, [visible, userId, initialTitle, initialBody]);

  const fetchDevices = async () => {
    if (!userId) return;

    setDevicesLoading(true);
    try {
      const response = await notificationsService.getUserPushTokens(userId);
      if (response.success && response.data) {
        setDevices(response.data.devices);
        setPermission(response.data.permission);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      message.error('Failed to fetch registered devices');
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleDeleteDevice = async (tokenId: string) => {
    setDeletingTokenId(tokenId);
    try {
      const response = await notificationsService.deletePushToken(tokenId);
      if (response.success) {
        message.success('Device removed successfully');
        // Remove from local state
        setDevices(prev => prev.filter(d => d.id !== tokenId));
      }
    } catch (error) {
      if (error instanceof ApiError) {
        message.error(error.message || 'Failed to remove device');
      } else {
        message.error('Failed to remove device');
      }
      console.error('Error deleting device:', error);
    } finally {
      setDeletingTokenId(null);
    }
  };

  const handleSend = async () => {
    if (!userId) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await notificationsService.sendNotification({
        userIds: [userId],
        title: values.title.trim(),
        body: values.body.trim(),
      });

      if (response.data) {
        message.success(
          `Notification sent successfully (${response.data.tokenCount} device${response.data.tokenCount !== 1 ? 's' : ''})`,
        );
        form.resetFields();
        onSent?.();
        onClose();
      }
    } catch (error) {
      if (error instanceof ApiError) {
        message.error(error.message || 'Failed to send notification');
      } else {
        message.error('Failed to send notification');
      }
      console.error('Error sending notification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const hasNoDevices = !devicesLoading && devices.length === 0;

  // No device, and the app's last permission report says why (MYG-724): "denied" = the user
  // installed the app but blocked notifications at system level, so no token was ever registered;
  // "granted" = the phone allowed it but token acquisition / registration failed.
  const blockedAtOsLevel = hasNoDevices && permission?.status === 'denied';
  const grantedButUnregistered = hasNoDevices && permission?.status === 'granted';

  // Check if all devices have notifications blocked
  const allDevicesBlocked =
    !devicesLoading && devices.length > 0 && devices.every(d => d.notificationStatus === 'PERMISSION_DENIED');

  // Combined check for send button
  const cannotSendNotification = hasNoDevices || allDevicesBlocked;

  // Build tooltip content for notification status
  const getStatusTooltipContent = (record: UserPushTokenDevice) => {
    const lines: string[] = [];

    const config = notificationStatusConfig[record.notificationStatus] || notificationStatusConfig.UNKNOWN;
    lines.push(config.description);

    if (record.lastDeliveryAt) {
      lines.push(`Last delivery: ${formatDateTime(record.lastDeliveryAt)}`);
    }

    if (record.lastDeliveryError) {
      lines.push(`Error: ${record.lastDeliveryError}`);
    }

    if (record.failureCount > 0) {
      lines.push(`Failed attempts: ${record.failureCount}`);
    }

    return lines.join('\n');
  };

  const columns = [
    {
      title: 'Device',
      key: 'device',
      render: (_: unknown, record: UserPushTokenDevice) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {record.deviceType === 'ios' ? (
            <AppleOutlined style={{ fontSize: 18 }} />
          ) : (
            <AndroidOutlined style={{ fontSize: 18, color: '#3DDC84' }} />
          )}
          <span>{record.deviceName || 'Unknown Device'}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: unknown, record: UserPushTokenDevice) => {
        const config = notificationStatusConfig[record.notificationStatus] || notificationStatusConfig.UNKNOWN;
        return (
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{getStatusTooltipContent(record)}</span>}>
            <Tag color={config.color} icon={config.icon}>
              {config.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Registered',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: Date) => formatDateTime(date),
    },
    {
      title: 'Last Delivery',
      key: 'lastDelivery',
      render: (_: unknown, record: UserPushTokenDevice) => {
        if (!record.lastDeliveryAt) {
          return <Tag>Never</Tag>;
        }
        const isSuccess = record.lastDeliveryStatus === 'success';
        return (
          <Tooltip title={record.lastDeliveryError || (isSuccess ? 'Delivered successfully' : 'Delivery failed')}>
            <span style={{ color: isSuccess ? '#52c41a' : '#ff4d4f' }}>{formatDateTime(record.lastDeliveryAt)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_: unknown, record: UserPushTokenDevice) => (
        <Popconfirm
          title="Remove device"
          description="Are you sure you want to remove this device?"
          onConfirm={() => handleDeleteDevice(record.id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} loading={deletingTokenId === record.id} size="small" />
        </Popconfirm>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'send',
      label: 'Send Notification',
      children: (
        <>
          {(blockedAtOsLevel || allDevicesBlocked) && (
            <Alert
              message="Notifications blocked on the user's phone"
              description={`${
                blockedAtOsLevel
                  ? `The app reported that notifications for mygate are turned off in the phone's system settings${lastReportedSuffix(permission)}.`
                  : 'All registered devices have notifications blocked.'
              } The user has to allow notifications for mygate in the system settings; the app registers the device on its next launch, no reinstall needed.`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {grantedButUnregistered && (
            <Alert
              message="Permission granted, but no device registered"
              description={`The app reported the OS permission as granted${lastReportedSuffix(permission)} but never registered a push token — token acquisition or registration failed on the phone (no Google Play services, offline during login, …). Ask the user to reopen the app; check the mobile logs if it persists.`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {hasNoDevices && !blockedAtOsLevel && !grantedButUnregistered && (
            <Alert
              message="No registered devices"
              description="This user has no registered devices. Notifications cannot be sent."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {userInfo && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
              <div>
                <strong>Sending to:</strong> {userInfo.name || 'Unknown User'}
              </div>
              {userInfo.phoneNumber && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>{userInfo.phoneNumber}</div>
              )}
            </div>
          )}

          <Form form={form} layout="vertical" requiredMark="optional">
            <Form.Item
              name="title"
              label="Title"
              rules={[
                { required: true, message: 'Please enter a title' },
                { max: 100, message: 'Title must be less than 100 characters' },
              ]}
            >
              <Input placeholder="Enter notification title" maxLength={100} showCount />
            </Form.Item>

            <Form.Item
              name="body"
              label="Message"
              rules={[
                { required: true, message: 'Please enter a message' },
                { max: 500, message: 'Message must be less than 500 characters' },
              ]}
            >
              <TextArea
                placeholder="Enter notification message"
                rows={4}
                maxLength={500}
                showCount
                style={{ resize: 'none' }}
              />
            </Form.Item>
          </Form>
        </>
      ),
    },
    {
      key: 'devices',
      label: `Registered Devices${devices.length > 0 ? ` (${devices.length})` : ''}`,
      children: (
        <div style={{ minHeight: 200 }}>
          {devicesLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : devices.length === 0 ? (
            <Empty
              image={<MobileOutlined style={{ fontSize: 64, color: '#d9d9d9', display: 'block', margin: '0 auto' }} />}
              description={noDevicesDescription(permission)}
            />
          ) : (
            <Table dataSource={devices} columns={columns} rowKey="id" pagination={false} size="small" />
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Push Notification"
      open={visible}
      onCancel={handleCancel}
      footer={
        activeTab === 'send'
          ? [
              <Button key="cancel" onClick={handleCancel}>
                Cancel
              </Button>,
              <Button
                key="send"
                type="primary"
                icon={<SendOutlined />}
                loading={loading}
                onClick={handleSend}
                disabled={cannotSendNotification}
              >
                Send Notification
              </Button>,
            ]
          : [
              <Button key="close" onClick={handleCancel}>
                Close
              </Button>,
            ]
      }
      width={600}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Modal>
  );
}
